import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { FiBell, FiCheckCircle, FiArrowRight } from "react-icons/fi";
import { BsDot } from "react-icons/bs";
import { primaryColor } from "../styles/color";
import {
  getPaymentReleasedDisplay,
  isPaymentReleasedNotification,
  isReviewNotification,
} from "../../../utils/paymentReleasedNotification";

const NotificationBell = ({
  notifications = [],
  borderColor = "#ccc",
  compact = false,
  onNavigate,
  onNotificationClick,
  onMarkAllRead,
}) => {
  const [open, setOpen] = useState(false);
  const bellRef = useRef(null);

  const toggleDropdown = () => setOpen((prev) => !prev);

  const handleClickOutside = (e) => {
    if (bellRef.current && !bellRef.current.contains(e.target)) {
      setOpen(false);
    }
  };

  const handleItemClick = async (notif) => {
    setOpen(false);
    let skipNav = false;
    if (typeof onNotificationClick === "function") {
      const result = await Promise.resolve(onNotificationClick(notif));
      skipNav = result === false;
    }
    const url = notif?.url;
    if (skipNav || !url) return;
    if (typeof onNavigate === "function") {
      onNavigate(url);
      return;
    }
    window.location.href = url;
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const hasUnread = notifications.some((n) => !n.read_at);

  return (
    <BellWrapper ref={bellRef}>
      <BellButton
        type="button"
        onClick={toggleDropdown}
        borderColor={borderColor}
        $compact={compact}
        aria-label="Notifications"
        aria-expanded={open}
      >
        <FiBell size={compact ? 17 : 20} aria-hidden />
        {hasUnread && <NotificationDot />}
      </BellButton>

      {open && (
        <Dropdown>
          {notifications.length === 0 ? (
            <DropdownItem type="button">No notifications</DropdownItem>
          ) : (
            notifications.map((notif, idx) => (
              <DropdownItem
                key={notif.id ?? idx}
                type="button"
                $unread={!notif.read_at}
                onClick={() => handleItemClick(notif)}
              >
                {isPaymentReleasedNotification(notif) ? (
                  (() => {
                    const display = getPaymentReleasedDisplay(notif);
                    return (
                      <PaymentReleasedCard>
                        <PaymentReleasedIcon>
                          <FiCheckCircle size={22} aria-hidden />
                        </PaymentReleasedIcon>
                        <PaymentReleasedTitle>
                          {notif.title || "Payment released"}
                        </PaymentReleasedTitle>
                        <PaymentReleasedSub>{display.subtitle}</PaymentReleasedSub>
                        <PaymentReleasedBreakdown>
                          {display.rows.length ? (
                            display.rows.map(([k, v]) => (
                              <PaymentReleasedRow key={k}>
                                <span>{k}</span>
                                <strong>{v}</strong>
                              </PaymentReleasedRow>
                            ))
                          ) : (
                            <PaymentReleasedRow>
                              <span>Open to view details</span>
                              <strong>
                                View{" "}
                                <FiArrowRight size={14} aria-hidden />
                              </strong>
                            </PaymentReleasedRow>
                          )}
                        </PaymentReleasedBreakdown>
                        {display.ctaLabel ? (
                          <PaymentReleasedCta>
                            {display.ctaLabel}{" "}
                            <FiArrowRight size={16} aria-hidden />
                          </PaymentReleasedCta>
                        ) : null}
                      </PaymentReleasedCard>
                    );
                  })()
                ) : isReviewNotification(notif) ? (
                  <ReviewCard>
                    <ReviewTitle>
                      {notif.title || "Review"}
                      {notif.metadata?.stars ? (
                        <ReviewStars>
                          {"★".repeat(Math.min(5, Number(notif.metadata.stars)))}
                        </ReviewStars>
                      ) : null}
                    </ReviewTitle>
                    <ReviewBody>{notif.message || notif.body}</ReviewBody>
                  </ReviewCard>
                ) : (
                  <>
                    <NotifHeader>
                      <BsDot size={22} color={primaryColor} />
                      {notif.title}
                    </NotifHeader>
                    {(notif.message || notif.body) && (
                      <NotifBody>{notif.message || notif.body}</NotifBody>
                    )}
                  </>
                )}
                {notif.time && <NotifFooter>{notif.time}</NotifFooter>}
              </DropdownItem>
            ))
          )}
          {typeof onMarkAllRead === "function" && notifications.length > 0 && (
            <MarkAllFooter type="button" onClick={() => { setOpen(false); onMarkAllRead(); }}>
              Mark all as read
            </MarkAllFooter>
          )}
        </Dropdown>
      )}
    </BellWrapper>
  );
};

export default NotificationBell;

const BellWrapper = styled.div`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
`;

const BellButton = styled.button`
  box-sizing: border-box;
  width: ${({ $compact }) => ($compact ? "36px" : "40px")};
  height: ${({ $compact }) => ($compact ? "36px" : "40px")};
  min-width: ${({ $compact }) => ($compact ? "36px" : "40px")};
  min-height: ${({ $compact }) => ($compact ? "36px" : "40px")};
  padding: 0;
  margin: 0;
  border: ${({ $compact }) => ($compact ? "1px" : "2px")} solid ${({ borderColor }) => borderColor || "#ccc"};
  border-radius: ${({ $compact }) => ($compact ? "8px" : "50%")};
  background: white;
  cursor: pointer;
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 0;
  color: #555;

  &:hover {
    background: #f5f6fa;
  }

  svg {
    display: block;
    flex-shrink: 0;
  }
`;

const NotificationDot = styled.div`
  position: absolute;
  top: 5px;
  right: 5px;
  width: 10px;
  height: 10px;
  background-color: #ff3b3b;
  border-radius: 50%;
`;

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: min(360px, 92vw);
  max-height: 420px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.12);
  z-index: 1000;
`;

const DropdownItem = styled.button`
  width: 100%;
  border: 0;
  border-bottom: 1px solid #f1f5f9;
  background: ${({ $unread }) => ($unread ? "#f8fafc" : "#fff")};
  padding: 12px 14px;
  text-align: left;
  cursor: pointer;
  &:hover {
    background: #f1f5f9;
  }
  &:last-child {
    border-bottom: 0;
  }
`;

const MarkAllFooter = styled.button`
  width: 100%;
  border: 0;
  border-top: 1px solid #e5e7eb;
  background: #fff;
  padding: 10px 14px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: ${primaryColor};
  cursor: pointer;
  &:hover {
    background: #f8fafc;
  }
`;

const NotifHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 700;
  color: #111827;
  font-size: 14px;
`;

const NotifBody = styled.p`
  margin: 6px 0 0 26px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.45;
`;

const NotifFooter = styled.div`
  margin-top: 8px;
  font-size: 11px;
  color: #94a3b8;
`;

const PaymentReleasedCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PaymentReleasedIcon = styled.div`
  color: #16a34a;
`;

const PaymentReleasedTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: #111827;
`;

const PaymentReleasedSub = styled.div`
  font-size: 12px;
  color: #64748b;
  line-height: 1.4;
`;

const PaymentReleasedBreakdown = styled.div`
  margin-top: 4px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
`;

const PaymentReleasedRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  color: #475569;
  &:not(:last-child) {
    margin-bottom: 6px;
  }
  strong {
    color: #0f172a;
    font-weight: 700;
    text-align: right;
  }
`;

const ReviewCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ReviewTitle = styled.div`
  font-weight: 700;
  font-size: 14px;
  color: #111827;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ReviewStars = styled.span`
  color: #f59e0b;
  font-size: 13px;
  letter-spacing: 1px;
`;

const ReviewBody = styled.p`
  margin: 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.45;
`;

const PaymentReleasedCta = styled.div`
  margin-top: 4px;
  font-size: 12px;
  font-weight: 700;
  color: ${primaryColor};
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;
